import { GET, POST, POSTForm } from "./utils";

/**
 * Source document returned from listSources endpoint
 */
export interface SourceDocument {
    id: string;
    owner: string;
    name: string;
    width?: number;
    height?: number;
    annotations?: Record<string, any>;
    labels?: Record<string, any>;
    split?: string;
    lastSet?: number;
    r?: number;
    classes?: string[];
    batches?: string[];
    [key: string]: any;
}

/**
 * Batch data returned from getBatch endpoint
 */
export interface BatchData {
    id: string;
    name: string;
    images?: number;
    [key: string]: any;
}

/**
 * Dataset data
 */
export interface DatasetData {
    id: string;
    annotation: string;
    name?: string;
    url?: string;
    owner?: string;
    [key: string]: any;
}

/**
 * Get dataset by rapidDataId
 */
export async function getDatasetByRapidDataId(rapidDataId: string): Promise<DatasetData | null> {
    try {
        const response = await GET(`/api/dataset?rapidDataId=${encodeURIComponent(rapidDataId)}`);
        return response as DatasetData;
    } catch (error: any) {
        if (error?.status === 404) {
            return null;
        }
        throw error;
    }
}

/**
 * Get batch by name within a dataset
 */
export async function getBatchByName(
    datasetId: string,
    batchName: string
): Promise<BatchData | null> {
    try {
        const response = await GET(
            `/api/dataset/batch?datasetId=${encodeURIComponent(datasetId)}&batchName=${encodeURIComponent(batchName)}`
        );
        return response as BatchData;
    } catch (error: any) {
        if (error?.status === 404) {
            return null;
        }
        throw error;
    }
}

/**
 * List sources in a batch
 */
export async function listSourcesInBatch(
    datasetId: string,
    batchId: string
): Promise<SourceDocument[]> {
    try {
        const response = await GET(
            `/api/dataset/sources?datasetId=${encodeURIComponent(datasetId)}&batchId=${encodeURIComponent(batchId)}`
        );
        return (response as { sources: SourceDocument[] }).sources;
    } catch (error: any) {
        if (error?.status === 404) {
            return [];
        }
        throw error;
    }
}

/**
 * AI tool types for annotation tracking/billing
 */
export enum AiTools {
    BOX_PROMPTING = "box-prompting",
    TEXT_PROMPTING = "text-prompting",
    SMART_POLYGON = "smart-polygon",
    LABEL_ASSIST = "label-assist"
}

export type AiToolUsed = AiTools | false;

/**
 * Box annotation in the format expected by updateAnnotation endpoint
 */
export interface AnnotationBox {
    id: string;
    label: string;
    x: number | string;
    y: number | string;
    width: number | string;
    height: number | string;
    confidence?: number | null;
    points?: [number, number][];
    type?: "polygon" | "mask" | "box";
}

/**
 * Annotation data structure for updateAnnotation endpoint
 */
export interface AnnotationData {
    key: string;
    width: number;
    height: number;
    boxes: AnnotationBox[];
}

/**
 * Parameters for updateAnnotation
 */
export interface UpdateAnnotationParams {
    datasetId: string;
    imageId: string;
    group: string;
    annotation: AnnotationData;
    now?: number;
    nextAnnotationId?: string;
    expectedVersion?: number | null;
    dontMarkNull?: boolean;
    modelAssisted?: boolean;
    reversion?: boolean;
    aiToolUsed?: AiToolUsed;
    labelingSession?: object;
    tabId?: string;
}

/**
 * Response from updateAnnotation endpoint
 */
export interface UpdateAnnotationResponse {
    updated?: boolean;
    file?: string;
    datasetClasses?: Record<string, number>;
    version?: number;
    message?: string;
    error?: any;
}

/**
 * Update annotation for a source/image
 * Persists annotation changes to Firestore via the /query/updateAnnotation endpoint
 */
export async function updateAnnotation(
    params: UpdateAnnotationParams
): Promise<UpdateAnnotationResponse> {
    const {
        datasetId,
        imageId,
        group,
        annotation,
        now = Date.now(),
        nextAnnotationId,
        expectedVersion = null,
        dontMarkNull = false,
        modelAssisted = false,
        reversion = false,
        aiToolUsed = false,
        labelingSession = {},
        tabId
    } = params;

    // Build form data matching the endpoint's expected format
    const formData: Record<string, string> = {
        dataset: datasetId,
        image: imageId,
        group,
        annotation: JSON.stringify(annotation),
        now: String(now),
        dontMarkNull: String(dontMarkNull),
        modelAssisted: String(modelAssisted),
        reversion: String(reversion),
        aiToolUsed: String(aiToolUsed),
        labelingSession: JSON.stringify(labelingSession)
    };

    if (nextAnnotationId !== undefined) {
        formData.nextAnnotationId = nextAnnotationId;
    }

    if (expectedVersion !== null && expectedVersion !== undefined) {
        formData.expectedVersion = String(expectedVersion);
    }

    if (tabId) {
        formData.tabId = tabId;
    }

    return POSTForm("/query/updateAnnotation", formData);
}

// ------------------------------
// Approve Source (Improve flow)
// ------------------------------

/**
 * AI tool enum for approve source endpoint (matches backend schema)
 */
export enum ApproveSourceAiTool {
    TEXT_PROMPTING = "text_prompting",
    BOX_PROMPTING = "box_prompting"
}

/**
 * Annotation format for approve source endpoint
 */
export interface ApproveSourceAnnotation {
    image: { width: number; height: number };
    predictions: Array<{
        id?: string;
        detection_id?: string;
        x: number;
        y: number;
        width: number;
        height: number;
        class: string;
        class_id?: number;
        confidence?: number | null;
        type?: "polygon" | "mask" | "box";
        mask?: string;
        masks?:
            | { counts: string; size: [number, number] }
            | Array<{ counts: string; size: [number, number] }>;
        points?: Array<{ x: number; y: number } | [number, number]>;
    }>;
}

/**
 * Parameters for approving a source from the improve batch
 */
export interface ApproveSourceParams {
    workspaceId: string;
    datasetId: string;
    sourceId: string;
    batchId: string;
    annotation: ApproveSourceAnnotation;
    aiTool?: ApproveSourceAiTool;
}

/**
 * Response from approve source endpoint
 */
export interface ApproveSourceResponse {
    success: boolean;
    sourceId: string;
    removedFromBatch: boolean;
    addedToDataset: boolean;
}

/**
 * Approve a source from a batch in the Improve flow.
 *
 * This endpoint:
 * 1. Removes the source from the batch
 * 2. Adds it to the dataset (if not already there)
 * 3. Saves the approved annotations
 */
export async function approveSource(params: ApproveSourceParams): Promise<ApproveSourceResponse> {
    return POST("/api/dataset/approve-source", params);
}

// Cache for random source queries (1 hour TTL)
const RANDOM_SOURCE_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

// Cached inference results for random sources
export interface CachedInferenceResults {
    predictions: Record<string, Record<number, any[]>>; // fileId -> frameSegment -> predictions
    annotations: Record<string, Record<string, any[]>>; // fileId -> frameId -> annotations
    inferredDimensions?: { width: number; height: number };
}

interface RandomSourceCacheEntry {
    source: SourceDocument | null;
    timestamp: number;
}
const randomSourceCache = new Map<string, RandomSourceCacheEntry>();

// Separate cache for inference results, keyed by workflowUrl
// This cache has NO TTL - it's cleared when switching workflows
interface InferenceCacheEntry {
    results: CachedInferenceResults;
}
const inferenceResultsCache = new Map<string, InferenceCacheEntry>();

/**
 * Get a random source from a dataset by rapidDataId
 * Used to display a sample image in the deploy page when no media is present.
 * Results are cached for 1 hour per rapidDataId to avoid unnecessary API calls.
 */
export async function getRandomSource(rapidDataId: string): Promise<SourceDocument | null> {
    console.log("[getRandomSource] Called with rapidDataId:", rapidDataId);
    console.log("[getRandomSource] Current cache keys:", Array.from(randomSourceCache.keys()));

    // Check cache first
    const cached = randomSourceCache.get(rapidDataId);
    if (cached && Date.now() - cached.timestamp < RANDOM_SOURCE_CACHE_TTL_MS) {
        console.log(
            "[getRandomSource] CACHE HIT - Returning cached source for:",
            rapidDataId,
            "sourceId:",
            cached.source?.id
        );
        return cached.source;
    }

    console.log("[getRandomSource] CACHE MISS - Fetching from API for:", rapidDataId);

    try {
        const response = await GET(
            `/api/dataset/random-source?rapidDataId=${encodeURIComponent(rapidDataId)}`
        );
        const source = response as SourceDocument;

        console.log(
            "[getRandomSource] API returned source:",
            source?.id,
            "for rapidDataId:",
            rapidDataId
        );

        // Cache the result
        randomSourceCache.set(rapidDataId, { source, timestamp: Date.now() });

        console.log(
            "[getRandomSource] Cached source. New cache keys:",
            Array.from(randomSourceCache.keys())
        );

        return source;
    } catch (error: any) {
        if (error?.status === 404) {
            // Cache the null result too
            randomSourceCache.set(rapidDataId, { source: null, timestamp: Date.now() });
            return null;
        }
        throw error;
    }
}

/**
 * Get cached inference results for a workflow
 * Returns the inference results if they exist (no TTL - cleared on workflow change)
 * @param workflowUrl - The workflow URL to use as cache key
 */
export function getCachedInferenceResults(workflowUrl: string): CachedInferenceResults | null {
    console.log("[getCachedInferenceResults] Called with workflowUrl:", workflowUrl);
    console.log(
        "[getCachedInferenceResults] Current inference cache keys:",
        Array.from(inferenceResultsCache.keys())
    );

    if (!workflowUrl) {
        console.log("[getCachedInferenceResults] No workflowUrl provided - returning null");
        return null;
    }
    const cached = inferenceResultsCache.get(workflowUrl);
    if (cached) {
        console.log(
            "[getCachedInferenceResults] CACHE HIT for:",
            workflowUrl,
            "predictions keys:",
            Object.keys(cached.results.predictions)
        );
        return cached.results;
    }
    console.log("[getCachedInferenceResults] CACHE MISS for:", workflowUrl);
    return null;
}

/**
 * Store inference results for a workflow in the cache
 * This allows us to restore predictions when navigating back from /improve to /deploy
 * @param workflowUrl - The workflow URL to use as cache key
 */
export function setCachedInferenceResults(
    workflowUrl: string,
    inferenceResults: CachedInferenceResults
): void {
    console.log("[setCachedInferenceResults] Called with workflowUrl:", workflowUrl);
    console.log(
        "[setCachedInferenceResults] Inference results predictions keys:",
        Object.keys(inferenceResults.predictions)
    );
    console.log(
        "[setCachedInferenceResults] Inference results annotations keys:",
        Object.keys(inferenceResults.annotations)
    );

    if (!workflowUrl) {
        console.log("[setCachedInferenceResults] No workflowUrl provided, skipping cache");
        return;
    }
    inferenceResultsCache.set(workflowUrl, { results: inferenceResults });
    console.log(
        "[setCachedInferenceResults] Cached. New inference cache keys:",
        Array.from(inferenceResultsCache.keys())
    );
}

/**
 * Clear cached inference results for a specific workflow
 * Called when switching to a different workflow to prevent stale predictions
 * @param workflowUrl - The workflow URL to clear, or undefined to clear all
 */
export function clearCachedInferenceResults(workflowUrl?: string): void {
    if (workflowUrl) {
        console.log("[clearCachedInferenceResults] Clearing cache for:", workflowUrl);
        inferenceResultsCache.delete(workflowUrl);
    } else {
        console.log("[clearCachedInferenceResults] Clearing all inference cache");
        inferenceResultsCache.clear();
    }
}

// ------------------------------
// Continuous Improvement - Flush Training
// ------------------------------

/**
 * Flush (immediately dispatch) a pending CI training job for a dataset,
 * bypassing the debounce grace period.
 */
export async function flushCITrainingJob(
    datasetId: string,
    options?: { force?: boolean }
): Promise<void> {
    await POST(
        `/api/ci/flush/${encodeURIComponent(datasetId)}`,
        options?.force ? { force: true } : undefined
    );
}

/**

// ------------------------------
// Upload Source for Review (Deploy -> Improve flow)
// ------------------------------

/**
 * Parameters for uploading a source to the rapid-review batch.
 * Provide sourceId when the image already exists in the dataset (fast path — just adds to batch).
 * Provide imageUrl when the image needs to be uploaded (e.g. user-uploaded from Firebase Storage).
 */
export interface UploadSourceForReviewParams {
    rapidDataId: string;
    sourceId?: string;
    imageUrl?: string;
    annotation: ApproveSourceAnnotation;
}

/**
 * Response from upload source for review endpoint
 */
export interface UploadSourceForReviewResponse {
    sourceId: string;
}

/**
 * Upload a source image with predictions to the rapid-review batch.
 * Used by the "Needs editing?" button on the deploy page.
 */
export async function uploadSourceForReview(
    params: UploadSourceForReviewParams
): Promise<UploadSourceForReviewResponse> {
    return POST("/api/dataset/upload-source-for-review", params);
}
